import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Mail, Phone, Calendar, Building2, FileText, Briefcase } from 'lucide-react';
import { Button, subtleNeo } from '@brainsway/ui';
import { localizedName } from '@/utils/localized';
import { useUserDetail } from '../hooks';
import { ADMIN_COLORS } from '../../admin/utils/adminAnimations';

const ROLE_COLORS: Record<string, string> = {
  SUPER_ADMIN: 'bg-slate-800 text-white',
  ADMIN: 'bg-primary-500 text-white',
  MANAGER: 'bg-slate-50 text-slate-800',
  THERAPIST: 'bg-slate-50 text-slate-800',
};

export function UserDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: user, isLoading } = useUserDetail(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-neutral-500">{t('common.loading')}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-neutral-500">{t('common.notFound')}</p>
      </div>
    );
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={subtleNeo.fadeIn} className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => navigate('/admin/users')}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          {t('users.title')}
        </button>
        <ArrowRight className="h-3.5 w-3.5 text-neutral-400 rtl:rotate-180" />
        <h1 className="text-lg sm:text-2xl font-semibold tracking-wide">
          {user.firstName} {user.lastName}
        </h1>
      </div>

      {/* User info card */}
      <div
        className="rounded-3xl overflow-hidden glass-surface p-6"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div className="flex flex-wrap items-start gap-6">
          {/* Avatar */}
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-2xl font-bold text-primary-600">
            {(user.firstName?.[0] || '').toUpperCase()}
            {(user.lastName?.[0] || '').toUpperCase()}
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-medium text-neutral-800">{user.firstName} {user.lastName}</h2>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${ROLE_COLORS[user.role]}`}>
                {t(`users.roles.${user.role}`)}
              </span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                user.status === 'ACTIVE' ? 'bg-success-50 text-success-700' : 'bg-neutral-100 text-neutral-500'
              }`}>
                {t(`users.statuses.${user.status}`)}
              </span>
              {user.employmentRole && (
                <span className="flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                  <Briefcase className="h-3 w-3" />
                  {localizedName(user.employmentRole, i18n.language)}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-neutral-500">
              <span className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {user.email}
              </span>
              {user.phone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" /> {user.phone}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> {new Date(user.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div
          className="p-4 rounded-xl glass-surface"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            border: `1px solid ${ADMIN_COLORS.border}`,
            boxShadow: ADMIN_COLORS.shadow,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-xs font-bold text-slate-500 uppercase">{t('users.reports')}</p>
          </div>
          <p className="text-2xl font-bold text-slate-800">{user._count.reports}</p>
        </div>
        <div
          className="p-4 rounded-xl glass-surface"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            border: `1px solid ${ADMIN_COLORS.border}`,
            boxShadow: ADMIN_COLORS.shadow,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-xs font-bold text-slate-500 uppercase">{t('users.hospitals')}</p>
          </div>
          <p className="text-2xl font-bold text-slate-800">{user.userHospitals.length}</p>
        </div>
      </div>

      {/* Hospital assignments */}
      <div
        className="rounded-3xl overflow-hidden glass-surface"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div
          className="px-6 py-2.5"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderBottom: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          <h3 className="font-semibold text-xs sm:text-sm">{t('hospitals.assignedEmployees')}</h3>
        </div>
        <div className="px-6 py-4">
        {user.userHospitals.length > 0 ? (
          <div className="space-y-2">
            {user.userHospitals.map((assignment) => (
              <div key={assignment.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-neutral-50 px-3 py-2">
                <Building2 className="h-4 w-4 text-neutral-400" />
                <div className="flex-1">
                  <span className="text-xs font-medium text-neutral-700">{localizedName(assignment.hospital, i18n.language)}</span>
                  <span className="ms-2 text-xs text-neutral-400">{assignment.hospital.code} · {assignment.hospital.city}</span>
                </div>
                {assignment.isPrimary && (
                  <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                    {t('hospitals.primary')}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-neutral-400">{t('hospitals.noEmployees')}</p>
        )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="secondary" onClick={() => navigate('/admin/users')}>
          {t('common.back')}
        </Button>
      </div>
    </motion.div>
  );
}
